/**
 * Global React-Query error handlers (Theme 5 — failure resilience).
 *
 * Extracted from main.tsx so the "mutations always speak" rule is unit-testable
 * (main.tsx bootstraps the whole app and can't be rendered in isolation).
 *
 * QUERY errors: stay quiet on 4xx (often expected — not-found / permission) and
 * only toast on 5xx. Query hooks can still override with their own onError.
 *
 * MUTATION errors: ALWAYS surface a failure (a mutation is the user changing
 * data; a silent failure means they think it saved when it did not —
 * Christa-trust CRITICAL). The OLD handler only toasted on `status >= 500`, so a
 * pure network error (no `.status`) or any 4xx fell through silently whenever the
 * mutation hook lacked its own onError. The new rule speaks unless the mutation
 * defines its own onError (then that hook owns messaging — avoids double-toast).
 *
 * Independent of `mutations.retry: 0` (the 2026-05-30 duplicate-payment
 * decision): we surface failures, we do not retry them.
 */
import { toast } from 'sonner';

interface ErrorLike {
  status?: number;
  message?: string;
}

/**
 * Shared decision for a mutation's own onError toast: WHAT to say, and whether
 * it should persist. Added 2026-08-06 after a review from the operator's seat.
 *
 * Three cases, deliberately different:
 *
 *  - **No `.status` (a genuine network failure).** `fetchWithAuth` doesn't wrap
 *    `fetch`, so an offline browser throws a raw `TypeError: Failed to fetch`
 *    and hooks that surface `err.message` showed exactly that string. It means
 *    nothing to a bookkeeper. It also became MUCH more common the moment
 *    mutations moved to `networkMode: 'always'` — before that an offline
 *    mutation paused and replayed on reconnect, so she never saw it at all.
 *    Say something she can act on, and DON'T persist it: the condition is
 *    transient and the retry is obvious.
 *
 *  - **5xx.** A server hiccup is also transient. These paths previously fell
 *    through to the global handler's friendly, auto-dismissing message; giving
 *    a hook its own onError accidentally made a blip permanent.
 *
 *  - **4xx.** The server is telling her something specific and true ("Cannot
 *    mark invoice as ready or sent with no line items"). THAT is worth
 *    `duration: Infinity` — it's the case the persistent toast was introduced
 *    for, because a rejected change that flashes and vanishes reads as a silent
 *    loss.
 */
/**
 * A signed-out session and a dropped connection both surface as an error with
 * no `.status` — `getAuthToken` (lib/api.ts:234) throws a plain
 * `Error('No authenticated user')`, and Firebase's own token refresh throws
 * `auth/...` codes the same way. Telling someone whose session expired to check
 * their internet sends them to the wrong fix entirely.
 */
function isAuthError(err: ErrorLike): boolean {
  const msg = err?.message || '';
  return /no authenticated user/i.test(msg) || /\bauth\/[a-z-]+/i.test(msg);
}

export function mutationErrorToast(error: unknown, fallback: string): void {
  const err = error as ErrorLike;

  if (!err?.status && isAuthError(err)) {
    toast.error(
      "You've been signed out — sign in again and retry. Your change wasn't saved.",
      { duration: Infinity },
    );
    return;
  }

  if (!err?.status) {
    toast.error(
      "You appear to be offline — your change wasn't saved. " +
        'Reconnect and try again.',
    );
    return;
  }
  if (err.status >= 500) {
    toast.error('Service temporarily unavailable — please try again in a moment');
    return;
  }
  toast.error(err.message || fallback, { duration: Infinity });
}

/** QUERY cache onError — 5xx only. */
export function handleGlobalQueryError(error: unknown): void {
  const err = error as ErrorLike;
  if (err?.status && err.status >= 500) {
    toast.error('Service temporarily unavailable — please try again in a moment');
  }
}

/**
 * MUTATION cache onError. 4th arg is the mutation; if it defines its own
 * onError, that hook owns user messaging and we stay quiet.
 */
export function handleGlobalMutationError(
  error: unknown,
  _variables?: unknown,
  _context?: unknown,
  mutation?: { options?: { onError?: unknown } },
): void {
  // The mutation defines its own onError → it owns the user-facing message.
  if (mutation?.options?.onError) return;

  const err = error as ErrorLike;
  if (err?.status && err.status >= 500) {
    toast.error('Service temporarily unavailable — please try again in a moment');
    return;
  }
  // Pure network error (no status) OR any 4xx with no local handler: speak up.
  toast.error('Your change was not saved — please try again.');
}
